import React, { useCallback } from "react";

import {
  PaymentPlan,
  PaymentSchedule,
  PaymentMethod,
  useInitiatePolicyPurchaseMutation,
  useRemovePaymentQuoteMutation,
} from "@covertree/new-business-graphql";
import {
  PurchaseAffiliation,
  PurchaseCheckoutOption,
  useTracking,
} from "@covertree/tracking";
import { Toast } from "@covertree/utils";

import { MortgageBillPaymentForm } from "./MortgageBillPaymentForm";
import { MortgageBillFormState } from "./MortgageBillPaymentForm/form";
import { useCheckout } from "../../CheckoutProvider";
import { getTotalCostFromPaymentPlan } from "../../utils";
import {
  mapAdditionalInterestToPayload,
  mapLenderToBillingAdditionalInterest,
  mapLenderToLenderDetails,
} from "../../AdditionalInterest";

interface Props {
  selectedPaymentPlan: PaymentPlan;
}

export function MortgageBillPayment({ selectedPaymentPlan }: Props) {
  const {
    anythingLoading,
    forceAgentSignResponsibility,
    isD2C,
    policyLocator,
    onError,
    setPaymentLoading,
    onPaymentSuccess,
    additionalInterests,
    isAdditionalInterestsEnabled,
  } = useCheckout();

  const [initiatePolicyPurchase] = useInitiatePolicyPurchaseMutation();
  const [removePaymentQuote] = useRemovePaymentQuoteMutation();

  const { trackPurchase } = useTracking();

  const onSubmit = useCallback(
    async ({
      lender,
      loanNumber,
      agentSignResponsibility,
    }: MortgageBillFormState) => {
      const signResponsibilityChecked = isD2C
        ? undefined
        : !!agentSignResponsibility;

      try {
        setPaymentLoading(true);

        const commonMortgageBillPayload = {
          policyLocator,
          paymentSchedule: PaymentSchedule.MortgageBill,
          paymentMethod: PaymentMethod.MortgageBill,
          signResponsibilityChecked,
        };

        const mortgageBillPayload = isAdditionalInterestsEnabled
          ? {
              ...commonMortgageBillPayload,
              additionalInterests: [
                mapLenderToBillingAdditionalInterest(lender, loanNumber),
                ...mapAdditionalInterestToPayload(additionalInterests),
              ],
            }
          : {
              ...commonMortgageBillPayload,
              lenderDetails: mapLenderToLenderDetails(lender, loanNumber),
            };

        const { data } = await initiatePolicyPurchase({
          variables: mortgageBillPayload,
        });
        if (data?.initiatePolicyPurchase.succeeded) {
          trackPurchase({
            affiliation: isD2C
              ? PurchaseAffiliation.D2C
              : PurchaseAffiliation.Agent,
            checkout_option: PurchaseCheckoutOption.MortgageBill,
            transaction_id: policyLocator,
            value: getTotalCostFromPaymentPlan(selectedPaymentPlan),
          });

          setPaymentLoading(false);
          onPaymentSuccess(signResponsibilityChecked);
        } else {
          throw new Error("Failed to initiate policy purchase");
        }
      } catch (error) {
        try {
          await removePaymentQuote({
            variables: {
              policyLocator,
            },
          });
        } catch {
          // We exit the screen to force user to reload checkout screen and therefore initiatePolicyPurchase once again
          // It tries to clear quotes on the beginning of execution
          setPaymentLoading(false);
          onError(error as Error);
          return;
        }

        setPaymentLoading(false);
        Toast.showError("Something went wrong, please try again.");
      }
    },
    [
      initiatePolicyPurchase,
      isD2C,
      onError,
      onPaymentSuccess,
      policyLocator,
      removePaymentQuote,
      setPaymentLoading,
      selectedPaymentPlan,
      trackPurchase,
      additionalInterests,
      isAdditionalInterestsEnabled,
    ]
  );

  return (
    <MortgageBillPaymentForm
      forceAgentSignResponsibility={forceAgentSignResponsibility}
      disabled={anythingLoading}
      onSubmit={onSubmit}
      isD2C={isD2C}
    />
  );
}
